import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { login, portalErrorMessage } from '../../features/portal/portalService';

const PortalLogin = ({ info, eventId, kind, venueId, onLoggedIn, onInvalid }) => {
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lockUntil, setLockUntil] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!lockUntil || lockUntil <= Date.now()) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [lockUntil]);

  const lockSeconds = Math.max(0, Math.ceil((lockUntil - now) / 1000));
  const locked = lockSeconds > 0;
  const configured = Boolean(info) && info.configured !== false;
  const canSubmit = configured && !locked && !submitting && /^\d{5}$/.test(pin);

  useEffect(() => {
    if (lockUntil && now >= lockUntil) {
      setLockUntil(0);
      setError('');
    }
  }, [lockUntil, now]);

  const title = useMemo(() => {
    if (kind === 'secretary') return 'Tournament Secretary';
    return info?.venueName || 'Club Portal';
  }, [kind, info]);

  const coverage = useMemo(() => {
    if (!info) return '';
    if (!info.shared) return 'This PIN is for this event only.';

    const noWorkspace = info.workspaceScope === 'none';
    if (kind === 'secretary') {
      return noWorkspace
        ? 'This PIN works at every event that is not part of a workspace. After signing in, use the event picker to switch.'
        : 'This PIN works at every event in this workspace. After signing in, use the event picker to switch.';
    }

    return noWorkspace
      ? 'This PIN works at every event at this club that is not part of a workspace. After signing in, use the event picker to switch.'
      : 'This PIN works at every event at this club in this workspace. After signing in, use the event picker to switch.';
  }, [info, kind]);

  const handlePinChange = (event) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 5);
    setPin(digits);
    if (error) setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      const data = await login({
        eventId,
        kind,
        venueId,
        pin
      });
      onLoggedIn?.(data);
    } catch (requestError) {
      const httpStatus = requestError?.response?.status;
      if (httpStatus === 404 || httpStatus === 410) {
        onInvalid?.();
        return;
      }

      const data = requestError?.response?.data;
      const retryAfter = Number(data?.retryAfterSeconds);

      if (Number.isFinite(retryAfter) && retryAfter > 0) {
        setLockUntil(Date.now() + retryAfter * 1000);
        setNow(Date.now());
      }

      setError(data?.message || portalErrorMessage(requestError, 'The PIN could not be checked.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="background.default"
      px={2}
      py={4}>
      <Paper sx={{ p: { xs: 2.5, sm: 4 }, width: '100%', maxWidth: 430 }} elevation={3}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <LockOutlinedIcon color="primary" fontSize="large" />
          <Typography variant="h5" fontWeight="bold" textAlign="center" mt={1}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={0.5}>
            {info?.event?.name || 'BowlScout event'}
          </Typography>
        </Box>

        {!configured && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This portal has not been set up by the organiser yet. Ask them to generate a PIN for
            this {kind === 'secretary' ? 'workspace portal' : 'club'}.
          </Alert>
        )}

        {coverage && (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 2 }}>
            {coverage}
          </Typography>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="5-digit PIN"
            value={pin}
            onChange={handlePinChange}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            fullWidth
            disabled={submitting || locked || !configured}
            inputProps={{ maxLength: 5, pattern: '[0-9]*' }}
            helperText={
              info?.personalAvailable
                ? 'Enter the personal PIN sent to you, or the shared portal PIN.'
                : 'Ask the organiser for the portal PIN.'
            }
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity={locked ? 'warning' : 'error'} sx={{ mb: 2 }}>
              {locked
                ? `Too many attempts. Try again in ${lockSeconds} second${lockSeconds === 1 ? '' : 's'}.`
                : error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={!canSubmit}
            sx={{ py: 1.4 }}>
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Open portal'}
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={2}>
          Ask the tournament organiser if you do not have a PIN. Your device stays signed in until
          you log out or the PIN is replaced.
        </Typography>
      </Paper>
    </Box>
  );
};

export default PortalLogin;
